import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useConfig } from "../../utils/config";
import { generateAICode } from "../../ai/CodeAI";
import { generateName } from "../../ai/NameAI";
import example from "./utils/exampleCode.json";
import { v4 as uuid } from 'uuid';
import { useGame, GameHistoryEntry } from "../../backend/useGame";
import { useGameArt } from "../../backend/useGameArt";
import { formatCode, extractCodeForAI, CodeFormat, AIFormat, GithubFormat, SandpackFormat, FileList } from "./utils/formatCode";
import { getTemplate } from "../../utils/template";
import { singleFile, multiFile, singleFilesForAIList, multiFilesForAIList } from "./utils/templates";
import GameInitial from "./views/GameInitial";
import GameEdit from "./views/GameEdit";
import { useUser } from "../../backend/useUser";
import { DeviceType } from "./components/GamePreview";
import { StreamContextType } from "./components/stream/helpers/Types";
import { StreamContextProvider, useStreamContext } from "./components/stream/helpers/StreamContext";
import { resetStreamUIState, setActiveSession, setAwaitingFirstChunk } from "./components/stream/helpers/streamStore";
import { getStreamUIState, setStreamUIState } from './components/stream/helpers/streamStore';
import FreeTrialTrigger from "../pricing/FreeTrialTrigger";
import { navigateToWebsite } from "../../utils/urls";

export type GameModes = 'Game' | 'Code' | 'Play' | 'Plan';

const CODE_CHANGE_LOAD_TIME = 100;

const GameInner = () => {
    const config = useConfig();
    const gameArtApi = useGameArt();
    const { user, loading: userLoading } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const { gameId: urlGameId } = useParams();
    const [instruction, setInstruction] = useState('');
    const [code, setCodeState] = useState<GithubFormat>(config.CODE_FILES_MODE === 'multiple' ? multiFile : singleFile);
    const [originalCode, setOriginalCode] = useState<GithubFormat>(config.CODE_FILES_MODE === 'multiple' ? multiFile : singleFile);
    const [filesForAIList, setFilesForAIList] = useState<FileList>(config.CODE_FILES_MODE === 'multiple' ? multiFilesForAIList : singleFilesForAIList);
    const [selectedHistoryIndex, setSelectedHistoryIndex] = useState(0);
    const [latestKnownEntryId, setLatestKnownEntryId] = useState<string | null>(null);
    const [mode, setMode] = useState<GameModes>('Game');
    const [iframeErrors, setIframeErrors] = useState<string[]>([]);
    const [historyEntryId, setHistoryEntryId] = useState('');
    const [history, setHistory] = useState<Partial<GameHistoryEntry>[]>([]);
    const [view, setView] = useState<'initial' | 'edit'>(urlGameId ? 'edit' : 'initial');
    const isNavigatingRef = useRef(false);
    const { streamContext } = useStreamContext();
    const capturedStreamContextRef = useRef<StreamContextType | null>(null);
    const pendingStreamCtxResolveRef = useRef<((c: StreamContextType) => void) | null>(null);
    const [gameName, setGameName] = useState('...');
    const [currentGameId, setCurrentGameId] = useState<string | null>(urlGameId);
    const [currentPlan, setCurrentPlan] = useState<string | null>("{ \"plan\": [], \"status\": null }"); // \"Loading\"
    const game = useGame();
    const [aiToggle, setAiToggle] = useState(false);
    const [isGeneratingName, setIsGeneratingName] = useState(false);
    const [sandpackKey, setSandpackKey] = useState(`${currentGameId}-${historyEntryId}`);
    const [codeForAI, setCodeForAI] = useState<AIFormat>(
        useMemo(() => extractCodeForAI(code, filesForAIList) as AIFormat, [code, filesForAIList])
    );
    const didInitialLoadRef = useRef(false);
    const [devicePreview, setDevicePreview] = useState<DeviceType>('computer');
    const [isGeneratingGame, setIsGeneratingGame] = useState(false);
    const [isUpdatingGameArt, setIsUpdatingGameArt] = useState(false);
    const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [gameArtTriggerReload, setGameArtTriggerReload] = useState<number>(0);
    const [realtimeArtEvent, setRealtimeArtEvent] = useState<{ row: any; type: 'INSERT'|'UPDATE'|'DELETE' } | null>(null);
    const [gameCreateCompleted, setGameCreateCompleted] = useState(false);
    const [updatesOccurredWhileAway, setUpdatesOccurredWhileAway] = useState(false);
    const lastRequestStartAtRef = useRef<number>(0);

    const [loadingMode, setLoadingMode] = useState(true);
    const [submittedHistoryId, setSubmittedHistoryId] = useState<string | null>(null);

    const currentCodeRef = useRef<GithubFormat>(code);

    const latestKnownEntryIdRef = useRef<string>('');
    const activeUrlGameIdRef = useRef<string | null>(urlGameId || null);

    useEffect(() => {
        activeUrlGameIdRef.current = urlGameId || null;
    }, [urlGameId]);

    // useEffect(() => {
    //     console.log('code:', code);
    // }, [code]);

    // Determine if undo button should be shown
    const canUndo = useMemo(() => {
        // Case 1: submittedHistoryId exists
        if (submittedHistoryId) {
            const submittedIndex = history.findIndex(entry => entry.id === submittedHistoryId);
            return submittedIndex !== -1;
        }

        // Case 2: Previous user_facing history entry exists
        const currentIndex = history.findIndex(entry => entry.id === historyEntryId);
        if (currentIndex > 0) {
            for (let i = currentIndex - 1; i >= 0; i--) {
                if (history[i].user_facing) {
                    return true;
                }
            }
        }
        return false;
    }, [history, historyEntryId, submittedHistoryId]);

    // Calculate latest user-facing history entry
    const latestUserFacingEntry = useMemo(() => {
        return history.slice().reverse().find(entry => entry.user_facing);
    }, [history]);

    const latestUserFacingHistoryId = latestUserFacingEntry?.id;
    const latestUserFacingCode = latestUserFacingEntry?.code;
    const latestUserFacingPrompt = latestUserFacingEntry?.prompt || '';
    const latestUserFacingStatus = latestUserFacingEntry?.status || '';
    const isViewingLatest = useMemo(() => {
        return !historyEntryId || (historyEntryId === latestUserFacingHistoryId);
    }, [historyEntryId, latestUserFacingHistoryId]);
    const selectedEntrySnapshot = useMemo(() => {
        if (!historyEntryId) return undefined;
        const entry = history.find(h => h.id === historyEntryId);
        return entry?.game_art_snapshot as any[] | undefined;
    }, [history, historyEntryId]);

    // useEffect(() => {
    //     console.log('gameCreateCompleted:', gameCreateCompleted);
    // }, [gameCreateCompleted]);

    // Load game if we have a game ID
    useEffect(() => {
        if (urlGameId && user) {
            // console.log('urlGameId', urlGameId);
            isNavigatingRef.current = true;
            setView('edit');
            loadGame(urlGameId).finally(() => {
                isNavigatingRef.current = false;
            });
        } else {
            // console.log('No game ID or user');
        }
        
        const unsubscribeFunction = game.subscribeToGameHistoryUpdates(urlGameId, () => {
            // console.log("[DB Subscription Callback] Triggered, calling loadGame for:", urlGameId);

            // Only load if code is different

            try {
                const id = activeUrlGameIdRef.current;
                if (!id) return;
                loadGame(id).then((stage) => {
                    // console.log("loadGame Completed in update");
                    
                    //Autorun for statuses that need it
                    // console.log("autoreload game data", stage);
                    if (stage) { // TODO: Confirm correct game id

                        // console.log("autoreload game data not null");
                        // const stage = stage; // TODO: Get status


                        // Removed auto-advance logic
                    }

                });
            } catch (error) {
                console.error('Error reloading game:', error);
            }
        })

        // Return cleanup function to be executed when component unmounts
        return () => {
            // Call the unsubscribe function if it exists
            if (unsubscribeFunction) {
                // console.log("unsubscribe from game history");
                unsubscribeFunction();
            }
        };
    }, [urlGameId, user]);

    // Game Art: realtime subscribe only when viewing latest user-facing entry and feature flag is enabled
    useEffect(() => {
        if (!config.USE_NEW_GAME_ART_HISTORY) return;
        if (!currentGameId) return;

        const isViewingLatest = !historyEntryId || (historyEntryId === latestUserFacingHistoryId);
        if (!isViewingLatest) return;

        const unsubscribe = gameArtApi.subscribeToGameArt(currentGameId, (row, type) => {
            // Apply fine-grained updates in the panel instead of full reloads
            setRealtimeArtEvent({ row, type });
        });
        return () => {
            unsubscribe && unsubscribe();
        };
    }, [config.USE_NEW_GAME_ART_HISTORY, currentGameId, historyEntryId, latestUserFacingHistoryId]);
    
    // Synchronized setter
    const setCode = (newCode: GithubFormat) => {
      currentCodeRef.current = newCode;
      setCodeState(newCode);
    };
    
    // Updated loadGame function
    const loadGame = async (requestedGameId: string, historyEntryId?: string, allowNonUserFacing?: boolean) => {
      // console.log("loadGame called with urlGameId:", requestedGameId, "historyEntryId:", historyEntryId);
      if (!requestedGameId) {
        // console.log("No urlGameId provided, returning early");
        return;
      }
      if (requestedGameId !== activeUrlGameIdRef.current) {
        // Route changed; ignore stale load
        return;
      }
      if (config.DEBUG_DISABLE_CODE_AI) {
        // console.log("DEBUG_DISABLE_CODE_AI is true, returning early");
        return;
      }

      try {
        // console.log("Attempting to get game with ID:", requestedGameId);
        const existingGame = await game.getGame(requestedGameId);
        // console.log("Retrieved existingGame:", existingGame);

        const checkGameOwner = async (existingGame: any, user: any) => {
          // console.log("checkGameOwner called with user ID:", user?.id);
          if (!user?.id) {
            console.warn('No user ID found, redirecting to home');
            navigate('/game');
            return null;
          }

          if (existingGame && existingGame.user_id !== user.id) {
            // console.log(`Game owner mismatch (game user_id: ${existingGame.user_id}, current user: ${user.id}), redirecting to play mode`);
            if (config.REDIRECT_TO_WEBSITE) {
                navigateToWebsite(`/play/${requestedGameId}`);
            } else {
                navigate(`/play/${requestedGameId}`);
            }
            
            return null;
          }
          // console.log('Game owner check passed');
          return existingGame;
        };
        const ownerCheckResult = checkGameOwner(existingGame, user);
        // console.log("Owner check result:", ownerCheckResult);
    
        if (existingGame && Array.isArray(existingGame.game_history_entries)) {
          // console.log("Existing game has history entries, processing...");
          setCurrentGameId(requestedGameId);
          setGameName(existingGame.game_name);
          const gameHistory = (existingGame.game_history_entries || [{id: 'starter', prompt: '', code: code, data: { plan: '', stage: '', stageType: '' }, user_facing: false }]) as Partial<GameHistoryEntry>[];
          // console.log("Processed gameHistory length:", gameHistory.length);
    
          // Set the current code to the last history entry
          const currentEntry = historyEntryId ? gameHistory.find(entry => entry.id === historyEntryId) : gameHistory[gameHistory.length - 1];
          // console.log("Selected currentEntry:", currentEntry?.id);
    
          if (currentEntry) {
            // console.log("Processing currentEntry data");
            
            // Parse data field if it's a string (DB stores it as JSON string)
            let dataObj: any = currentEntry.data;
            if (typeof dataObj === 'string') {
                try {
                    dataObj = JSON.parse(dataObj);
                } catch (e) {
                    console.error('[loadGame] Failed to parse data field:', e);
                    dataObj = { plan: '[]', stage: null, stageType: null };
                }
            }
            
            const planStatus = dataObj?.stage;
            // console.log('[loadGame] DB entry details:', {
            //     entry_id: currentEntry.id,
            //     user_facing: currentEntry.user_facing,
            //     has_data: !!currentEntry.data,
            //     data_was_string: typeof currentEntry.data === 'string',
            //     stage: planStatus,
            //     stageType: dataObj?.stageType,
            //     raw_data: currentEntry.data
            // });
            
            // During active streaming, only update if we have a valid status (not null/undefined)
            // This prevents race conditions where DB entry is created before backend sets the stage
            const { isStreaming } = getStreamUIState();
            if (isStreaming && !planStatus) {
                console.log('[loadGame] Skipping null/undefined status update during active streaming');
                // Don't update plan - preserve current in-flight status
            } else {
                setCurrentPlan(JSON.stringify({ plan: JSON.parse(dataObj?.plan || "[]"), status: planStatus, statusType: dataObj?.stageType}));
                // Fallback: if DB shows a terminal status, ensure stream ends
                scheduleStreamDoneFallbackIfTerminal(planStatus);
            }
          }
    
          // Find the most recent user-facing entry regardless of initial load state
          let gameEntry = currentEntry;
          if (currentEntry && !currentEntry.user_facing && !allowNonUserFacing) {
            // console.log("Current entry is not user-facing, looking for user-facing entry");
            const userFacingEntry = existingGame.game_history_entries.slice().reverse().find(x => x.user_facing);
            if (userFacingEntry) {
              // console.log("Found user-facing entry:", userFacingEntry.id);
              gameEntry = userFacingEntry;
            }
          }
    
          // console.log("Final gameEntry:", gameEntry?.id, "user_facing:", gameEntry?.user_facing);
    
          if (gameEntry) {

            if (!gameEntry.user_facing && !allowNonUserFacing) {
                return;
            }
              
            // Compare against STATE (not ref) for accurate comparison
            const currentCodeStr = JSON.stringify(currentCodeRef.current);
            const newCodeStr = JSON.stringify(gameEntry.code);
            // console.log("Code comparison - current:", currentCodeStr.length, "new:", newCodeStr.length, "equal:", currentCodeStr === newCodeStr);
            
            // Find the newest server entry (user-facing entries only)
            const serverLatestEntry = existingGame.game_history_entries
              .filter(entry => entry.user_facing)
              .reduce((latest, entry) => 
                (!latest || entry.created_at > latest.created_at) ? entry : latest, 
                null
              );
            // console.log("Server latest user-facing entry:", serverLatestEntry?.id);
        
            // Determine if we should refresh
            const shouldRefresh = 
                // Case 1: Explicit version requested - always load
                historyEntryId ? true :
                // Case 2: No latest known yet (initial load) - load if changed
                !latestKnownEntryIdRef.current ? (currentCodeStr !== newCodeStr) :
                // Case 3: Server has newer version than what we last knew about
                (serverLatestEntry?.id !== latestKnownEntryIdRef.current && 
                    gameEntry.id === serverLatestEntry?.id &&
                    currentCodeStr !== newCodeStr);
            
                // console.log("Should refresh:", shouldRefresh, "because:",
                //     historyEntryId ? "explicit version requested" :
                //     !latestKnownEntryIdRef.current ? "initial load with code change" :
                //     "server has newer version");
    
            if ((gameEntry.user_facing || allowNonUserFacing) && shouldRefresh) {
                // console.log("In Should refresh a");
                // console.log("Performing refresh with gameEntry:", gameEntry.id);
                // console.log("In Should refresh a");
                
                didInitialLoadRef.current = true;
                setLoadingMode(true);
                
                // Use synchronized setter to update both ref and state
                setIframeErrors([]);
                setCode(gameEntry.code as GithubFormat);
                setOriginalCode(gameEntry.code as GithubFormat);
                setCodeForAI(extractCodeForAI(gameEntry.code, filesForAIList) as AIFormat);
                setHistoryEntryId(gameEntry.id);
                setSelectedHistoryIndex(historyEntryId ? history.findIndex(h => h.id === historyEntryId) : gameHistory.length);
                setSandpackKey(prev => prev + 1);
        
                // Update latest known version if loading current version
                if (!historyEntryId && serverLatestEntry) {
                    // console.log("Updating latestKnownEntryId to:", serverLatestEntry.id);
                    // console.log("setting setLatestKnownEntryId a");
                    setLatestKnownEntryId(serverLatestEntry.id);
                    latestKnownEntryIdRef.current = serverLatestEntry.id;
                }
        
                if (existingGame.game_history_entries.some(entry => entry.user_facing && entry.status === 'SUCCESS')) {
                    // console.log("Found successful user-facing entry, setting gameCreateCompleted");
                    setGameCreateCompleted(true);
                }
                setMode('Game');
        
                // console.log("Setting loading timeout for:", CODE_CHANGE_LOAD_TIME);
                setTimeout(() => {
                    // console.log("Loading timeout complete, setting loadingMode to false");
                    setLoadingMode(false);
                }, CODE_CHANGE_LOAD_TIME);
            }
            
            // Build history array (code remains primary; UI can opt to use snapshot if needed)
            const historyArray = gameHistory.map(entry => ({
              id: entry.id,
              prompt: entry.prompt,
              code: entry.code as GithubFormat,
              plan: entry.data.plan,
              user_facing: entry.user_facing,
              created_at: entry.created_at,
              status: entry.status,
              game_art_snapshot: entry.game_art_snapshot,
            }));
            // console.log("Setting history with", historyArray.length, "entries");
            setHistory(historyArray);
          }
    
          if (currentEntry) {
            // console.log("Returning currentEntry stage:", currentEntry.data?.stage);
            return (currentEntry.data || {}).stage;
          }
          // console.log("No currentEntry, returning null");
          return null;
        } else {
          console.warn('No existing game found with ID:', requestedGameId, 'or game_history_entries is not an array');
          navigate('/game');
          return null;
        }
      } catch (error) {
        console.error('Error loading game:', error);
        navigate('/game');
        return null;
      }
    };

    useEffect(() => {
        setSandpackKey(`${currentGameId}-${historyEntryId}`);
    }, [currentGameId, historyEntryId]);

    // Polling mechanism for game generation status
    useEffect(() => {
        // Start polling when isGeneratingGame becomes true
        if (isGeneratingGame && urlGameId) {
            let pollCount = 0;
            const maxPolls = 60; // 10 minutes (60 polls * 10 seconds)
            
            // Clear any existing timer
            if (pollingTimerRef.current) {
                clearInterval(pollingTimerRef.current);
            }
            
            // Set up polling interval
            pollingTimerRef.current = setInterval(() => {
                // Check if we've reached the maximum polling duration
                if (pollCount >= maxPolls) {
                    if (pollingTimerRef.current) {
                        clearInterval(pollingTimerRef.current);
                        pollingTimerRef.current = null;
                    }
                    return;
                }
                
                // Call loadGame to check status
                const id = activeUrlGameIdRef.current;
                if (!id) return;
                loadGame(id).then(() => {
                    pollCount++;
                }).catch(error => {
                    console.error('Error during game generation polling:', error);
                });
            }, 10000); // Poll every 10 seconds
        } else if (!isGeneratingGame && pollingTimerRef.current) {
            // Stop polling when isGeneratingGame becomes false
            clearInterval(pollingTimerRef.current);
            pollingTimerRef.current = null;
        }
        
        // Cleanup function to clear interval when component unmounts
        return () => {
            if (pollingTimerRef.current) {
                clearInterval(pollingTimerRef.current);
                pollingTimerRef.current = null;
            }
        };
    }, [isGeneratingGame, urlGameId, loadGame]);
    
    // Replace the visibility change effect with this:
    useEffect(() => {
        if (urlGameId && view === 'edit') {
            const handleVisibilityChange = () => {
                if (document.visibilityState === 'visible') {
                    // Always load game data when returning to tab
                    const id = activeUrlGameIdRef.current;
                    const loadPromise = (id ? loadGame(id) : Promise.resolve()).catch(error => {
                        console.error('Error reloading game on tab focus:', error);
                    });
    
                    // Only show loading sequence if updates occurred while away
                    if (updatesOccurredWhileAway) {
                        setLoadingMode(true);
                        setSandpackKey(prev => prev + 1);
                        
                        Promise.all([
                            loadPromise,
                            new Promise(resolve => setTimeout(resolve, CODE_CHANGE_LOAD_TIME))
                        ]).finally(() => {
                            setLoadingMode(false);
                        });
                        
                        setUpdatesOccurredWhileAway(false);
                    }
                } else {
                    // When tab loses focus, prepare to detect updates
                    setUpdatesOccurredWhileAway(false);
                }
            };
    
            document.addEventListener('visibilitychange', handleVisibilityChange);
            return () => {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
            };
        }
    }, [urlGameId, view, loadGame, updatesOccurredWhileAway]);
    
    // Track code updates while tab is inactive
    useEffect(() => {
        if (urlGameId && view === 'edit' && document.visibilityState !== 'visible') {
            setUpdatesOccurredWhileAway(true);
        }
    }, [code, urlGameId, view]);

    

    const handleInstructionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        // console.log('Description changed to:', value);
        setInstruction(value);
    };

    const handleNameGeneration = useCallback(async (instruction: string) => {
        try {
            if (config.DEBUG_DISABLE_CODE_AI) {
                const id = uuid();
                setCurrentGameId(id);
                const newGame = {
                    id: id,
                    game_name: gameName,
                    game_history_entries: [
                        {
                            id: uuid(),
                            code: '',
                            prompt: '.',
                            status: 'SUCCESS',
                            game_id: id,
                            created_at: new Date(),
                            updated_at: new Date(),
                            user_facing: false
                        }
                    ]
                };
                return newGame;
            } else {
                setIsGeneratingName(true);
                const generatedName = await generateName({
                    prompt: instruction,
                    type: 'game'
                });
                // console.log('Generated game name:', generatedName);
                
                // Create the game immediately after generating the name
                // console.log('Creating new game with name:', generatedName);
                const newGame = await game.createGame(generatedName);
                // console.log('Created new game:', newGame);
                
                setGameName(generatedName);
                setCurrentGameId(newGame.id);
                navigate(`/game/${newGame.id}`);

                return newGame;
            }
        } finally {
            setIsGeneratingName(false);
        }
    }, [generateName, game]);

    useEffect(() => {
        // console.log('selectedHistoryIndex: ', selectedHistoryIndex);
    }, [selectedHistoryIndex]);

    const waitForStreamContextFromUI = useCallback(async (timeoutMs: number = 800): Promise<StreamContextType | null> => {
        if (capturedStreamContextRef.current) return capturedStreamContextRef.current;
        return new Promise<StreamContextType | null>((resolve) => {
            let settled = false;
            const timer = setTimeout(() => {
                if (!settled) {
                    settled = true;
                    resolve(null);
                }
            }, timeoutMs);
            pendingStreamCtxResolveRef.current = (ctx: StreamContextType) => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                resolve(ctx);
            };
        });
    }, []);

    const generateCode = useCallback(async (prompt: string, gameId: string | null = null, sessionId?: string) => {
        // console.log('Generate clicked: ', prompt);

        if (config.DEBUG_DISABLE_CODE_AI) {
            const raw = example[1].output;
            const extractedCode = formatCode(raw, CodeFormat.AI, CodeFormat.GITHUB) as GithubFormat;
            const extractedCodeForAI = extractCodeForAI(extractedCode, filesForAIList);
            return {raw: raw, code: extractedCode, codeForAI: extractedCodeForAI};
        }

        // For AI generation, include existing code if we're not in initial state

        //const fullPrompt = `${currentGameId ? 'BUGFIX:' : ''}\n${prompt}\n\nPLAN:\n${currentPlan}\n\nCURRENTCODE:${codeForAI}`;
        const fullPrompt = `${currentGameId ? 'EDIT: ' : ''}\n${prompt}`;
        // console.log('fullPrompt: ', fullPrompt);
        // TODO: Pass history

        try {
            // Preserve existing plan status if available, otherwise default to "Thinking"
            const existingPlan = JSON.parse(currentPlan || "{}");
            const statusToUse = existingPlan.status || "Thinking";
            setCurrentPlan(JSON.stringify({ plan: existingPlan.plan, status: statusToUse }));

            // Ensure the UI stream context is ready before starting the backend stream
            const ctxFromCallback = capturedStreamContextRef.current || (await waitForStreamContextFromUI(1000));

            const templateType = getTemplate(currentCodeRef.current);
            const response = await generateAICode({
                req: {
                    prompt: fullPrompt,
                    historyEntryId,
                    streamContext: ctxFromCallback || streamContext || undefined,
                    sessionId
                },
                config: config,
                gameId,
                isInitialCreate: view === 'initial',
                templateType
            });

            // Streaming completion will be handled by SSE approach
            
            if (response.raw === '') {
                // console.log('Code generation failed: empty response');
                return {raw: null, code: null, codeForAI: null};
            }

            // console.log('Code generation successful: ', response);

            const raw = response.raw;
            const plan = raw.split("PLAN:")[0].split("CODE:")[0];
            const extractedCode = formatCode(raw, CodeFormat.AI, CodeFormat.GITHUB) as GithubFormat;
            const extractedCodeForAI = extractCodeForAI(extractedCode, filesForAIList);

            return {raw: response.raw, code: extractedCode, codeForAI: extractedCodeForAI, plan};
        } catch (error) {
            console.error('Code generation failed: ', error);
            throw error;
        }
    }, [selectedHistoryIndex, code, config, currentGameId, game, waitForStreamContextFromUI, streamContext]);

    const incrementGameArtTriggerReload = () => {
        setGameArtTriggerReload(prev => (prev + 1) as number);
    };

    const handleGenerateFromUser = useCallback(async (instruction: string) => {
        // console.log("handleGenerateFromUser");
        // Clear any previous stream UI before starting a fresh generation
        try { resetStreamUIState(); } catch (_) {}

        // Immediately reflect a new in-flight request in plan and stream UI state
        try {
            // console.log('[handleGenerateFromUser] Setting plan status to Thinking for new request');
            setCurrentPlan(JSON.stringify({ plan: JSON.parse(currentPlan || "{}").plan, status: "Thinking" }));
        } catch (_) {
            setCurrentPlan(JSON.stringify({ plan: [], status: "Thinking" }));
        }
        try {
            setStreamUIState({ isStreaming: true, streamPhase: 'Connecting' });
        } catch (_) {}
        lastRequestStartAtRef.current = Date.now();
        // Clear the input box immediately upon submit
        setInstruction('');
        
        try {
            // console.log('🌊 Starting generation. streamContext present:', !!streamContext);
            let targetGameId = currentGameId;
            
            // Only generate name and create game in initial mode
            if (view === 'initial') {
                // console.log('Initial mode: generating name and creating game');
                setView('edit');
                setSelectedHistoryIndex(0);
                const newGame = await handleNameGeneration(instruction);
                targetGameId = newGame.id;
            }

            // If viewing a non-latest history entry, restore game_art to that snapshot before proceeding
            try {
                if (config.USE_NEW_GAME_ART_HISTORY && !isViewingLatest && selectedEntrySnapshot?.length && targetGameId) {
                    await gameArtApi.restoreGameArtToSnapshot(targetGameId, selectedEntrySnapshot as any[]);
                }
            } catch (e) {
                console.error('Error restoring game art snapshot before generate:', e);
                return;
            }

            // Create a new stream session id to track this run in UI store
            const sessionId = uuid();
            try { setActiveSession(sessionId); } catch (_) {}
            try { setAwaitingFirstChunk(true); } catch (_) {}

            // Generate the code
            // console.log('Generating code with game ID:', targetGameId);
            const {raw: newRaw, code: newCode, codeForAI: newCodeForAI, plan} = await generateCode(instruction, targetGameId, sessionId);
            // console.log('newRaw: ', newRaw);
            
            try {
                const parsedResponse = JSON.parse(newRaw);
                if (parsedResponse?.route === 'ART') {
                    if (!config.USE_NEW_GAME_ART_HISTORY) {
                        incrementGameArtTriggerReload();
                    }
                    // console.log("handleGenerateFromUser incrementGameArtTriggerReload b");
                }
            } catch (error) {
                // console.error('Error parsing response:', error);
            }

            if (!newCode) return;

            // setGameCreateCompleted(true); // Set state to true when initial generation is complete

            // setCode(newCode);
            // setCodeForAI(newCodeForAI);
            //const newHistoryId = uuid();
            //setHistory(prev => [...prev, { id: newHistoryId, prompt: instruction, code: newCode, raw: newRaw, plan, user_facing: false }]);
            //setHistoryEntryId(newHistoryId);
            //setIframeErrors([]);
            
            //if (view !== 'initial') {
                // console.log('history length:', history.length);
            //    setSelectedHistoryIndex(history.length);
            //}
        } catch (error) {
            console.error('Error in handleGenerateFromUser:', error);
        }
    }, [generateCode, historyEntryId, view, handleNameGeneration, currentGameId]);

    const handleIframeError = useCallback((errors: string) => {
        // Each call to handleIframeError is a new, single error
        const errorList = [errors].filter(e => e.trim() !== '');

        // Don't process empty errors
        if (errorList.length === 0) {
            // console.warn('Ignoring empty error list');
            return;
        }

        // Ensure unique errors and add the new one.
        const updatedIframeErrors = Array.from(new Set([...iframeErrors, ...errorList]));

        if (!arraysEqual(iframeErrors, updatedIframeErrors)) {
            // console.log('Iframe errors:', updatedIframeErrors);
            setIframeErrors(updatedIframeErrors);
        }

        // helper function to compare arrays
        function arraysEqual(a: string[], b: string[]): boolean {
            if (a === b) return true;
            if (a.length !== b.length) return false;

            for (let i = 0; i < a.length; ++i) {
                if (a[i] !== b[i]) return false;
            }
            return true;
        }

        fixErrors(errorList);
    }, [generateCode, code, iframeErrors]);

    const handleTryToFix = () => {
        const errorString = iframeErrors.join('\n');
        // console.log(errorString);
        handleGenerateFromUser(errorString);
    };

    // When the user starts generating art, clear the errors
    useEffect(() => {
        if (isUpdatingGameArt) {
            handleCloseErrorPopup();
        }
    }, [isUpdatingGameArt]);

    const fixErrors = (errorList: string[]) => {
        setInstruction('');

        // Get the current history entry's original prompt
        const currentIndex = history.findIndex(h => h.code === code);
        const originalPrompt = currentIndex >= 0 ? history[currentIndex].prompt : instruction;
        
        const prompt = `Fix these errors in the ${originalPrompt} game:\n${errorList.map((error, i) => `${i + 1}. ${error}`).join('\n')}`;
        generateCode(prompt);
    };

    const handleCloseErrorPopup = () => {
        setIframeErrors([]);
    };

    const refreshApp = useCallback(() => {
        // console.log('Refreshing app');
        handleCloseErrorPopup();
        setSandpackKey(prev => prev + 1);
    }, []);
    
    const onSelectHistoryState = useCallback(async (entry: { id: string; prompt: string; code: GithubFormat }) => {
        // console.log('Navigating to previous history state');
        isNavigatingRef.current = true;
        try {
            // Allow non-user-facing entries in debug mode when explicitly selecting a history entry
            const allowNonUserFacing = config.DEBUG_SHOW_IN_PROGRESS_HISTORY_ENTRIES;
            await loadGame(currentGameId, entry.id, allowNonUserFacing);
        } finally {
            // Ensure mode is set to 'Game' even if loadGame fails
            setMode('Game');
            isNavigatingRef.current = false;
        }
    }, [currentGameId, loadGame, config.DEBUG_SHOW_IN_PROGRESS_HISTORY_ENTRIES]);

    const handleUndoClick = useCallback(() => {
        if (history.length === 0) {
            return;
        }
    
        let targetEntryId: string | null = null;
    
        if (submittedHistoryId) {
            // console.log('Case 1: submittedHistoryId exists, try to go back to it');
    
            const submittedIndex = history.findIndex(entry => entry.id === submittedHistoryId);
            if (submittedIndex !== -1) {
                targetEntryId = submittedHistoryId;
            } else {
                // submittedHistoryId not found, fallback to previous
                const currentIndex = history.findIndex(entry => entry.id === historyEntryId);
                if (currentIndex > 0) {
                    targetEntryId = history[currentIndex - 1].id;
                }
            }
        } else {
            // console.log('Case 2: submittedHistoryId does not exist, go to the previous user_facing entry');
    
            const currentIndex = history.findIndex(entry => entry.id === historyEntryId);
            if (currentIndex > 0) {
                // Iterate backward to find the first user_facing entry
                for (let i = currentIndex - 1; i >= 0; i--) {
                    if (history[i].user_facing) {
                        targetEntryId = history[i].id;
                        break;
                    }
                }
            }
        }
    
        if (targetEntryId) {
            const targetEntry = history.find(entry => entry.id === targetEntryId);
            if (targetEntry) {
                onSelectHistoryState(targetEntry as { id: string; prompt: string; code: GithubFormat });
            }
        } else {
            console.warn('No suitable history entry found to undo to.');
        }
    }, [history, historyEntryId, submittedHistoryId, onSelectHistoryState]);

    const onToggleAI = () => {
        setAiToggle(!aiToggle);
    };

    // useEffect(() => {
    //     console.log('instruction:', instruction);
    // }, [instruction]);

    // useEffect(() => {
    //     console.log('History:', selectedHistoryIndex, historyEntryId, history);
    // }, [history, selectedHistoryIndex, historyEntryId]);

    const setInitialCode = async () => {
        // console.log('setInitialCode');
        const initialCode = config.CODE_FILES_MODE === 'multiple' ? multiFile : singleFile;
        const initialFileList = config.CODE_FILES_MODE === 'multiple' ? multiFilesForAIList : singleFilesForAIList;
        const initialCodeForAI = extractCodeForAI(initialCode, initialFileList) as AIFormat;

        setCode(initialCode);
        setOriginalCode(initialCode);
        setFilesForAIList(initialFileList);
        setCodeForAI(initialCodeForAI);
    };

    const onHomeClick = () => {
        // console.log('Home clicked.');
        try { resetStreamUIState(); } catch (_) {}

        // Reset all state variables to their initial values in a single batch
        setInitialCode();
        setInstruction('');
        setSelectedHistoryIndex(0);
        setMode('Game');
        setIframeErrors([]);
        setIframeErrors([]);
        setHistoryEntryId('');
        setHistory([]);
        setView('initial');
        setGameName('Game');
        setCurrentGameId(null);
        setIsGeneratingName(false);
        setCurrentPlan("{ \"plan\": [], \"status\": null }")
        setGameCreateCompleted(false);
        setLatestKnownEntryId(null);
        latestKnownEntryIdRef.current = '';
        setUpdatesOccurredWhileAway(false);
        setLoadingMode(true);
        setSubmittedHistoryId(null);
        latestKnownEntryIdRef.current = "";
        
        // Navigate to base route without game ID
        navigate('/game');
    };

    const changeGameName = async (newName: string) => {
        // console.log('Changing game name to:', newName);
        if (!currentGameId) {
            console.error('No current game ID available');
            return;
        }
        
        try {
            setGameName(newName);
            await game.updateGame(currentGameId, { game_name: newName });
        } catch (error) {
            console.error('Error updating game name:', error);
        }
    };

    const onDebugTemplateClick = () => {
        // setCode(consoleLogTemplate);
        setCode(config.CODE_FILES_MODE === 'multiple' ? multiFile : singleFile);
        setOriginalCode(config.CODE_FILES_MODE === 'multiple' ? multiFile : singleFile);
        setView('edit');
        setSelectedHistoryIndex(0);
        setSandpackKey(prev => prev + 1);
    };

    // useEffect(() => {
    //     console.log('CODE:', code);
    // }, [code]);

    // useEffect(() => {
    //     console.log('CODE FOR AI:', codeForAI);
    // }, [codeForAI]);

    // Handle saving changes to game balance
    const handleSaveLocalCodeChange = async (updatedCode: GithubFormat, action: 'balance' | 'code') => {
        if (!currentGameId) return;
        
        try {
            // If viewing a non-latest history entry, restore game_art to that snapshot before saving code
            try {
                if (config.USE_NEW_GAME_ART_HISTORY && !isViewingLatest && selectedEntrySnapshot?.length && currentGameId) {
                    await gameArtApi.restoreGameArtToSnapshot(currentGameId, selectedEntrySnapshot as any[]);
                }
            } catch (e) {
                console.error('Error restoring game art snapshot before save:', e);
                return;
            }

            // Create a new history entry with the updated code
            const prompt = action === 'balance' ? "Update game settings" : "Edit code";
            
            // Create a new history entry in the database with raw data from last entry
            const lastEntry = history.length > 0 ? history[history.length - 1] : null;
            
            const newHistoryEntry = await game.saveCodeToHistory(
                currentGameId,
                updatedCode,
                prompt
            );
            setLoadingMode(true);
            
            // Update local state
            setCode(updatedCode);
            setOriginalCode(updatedCode);
            setCodeForAI(extractCodeForAI(updatedCode, filesForAIList) as AIFormat);
            setHistoryEntryId(newHistoryEntry.id);
            setHistory(prev => [...prev, newHistoryEntry]);
            setSelectedHistoryIndex(history.length);
            setTimeout(() => {
                // console.log("Loading timeout complete, setting loadingMode to false");
                setLoadingMode(false);
            }, CODE_CHANGE_LOAD_TIME);
        } catch (error) {
            console.error('Error saving game balance changes:', error);
        }
    };

    const handleDeviceClick = () => {
        setDevicePreview(devicePreview === 'computer' ? 'Mobile' : 'computer');
    };

    const handleAIStatusChange = (isRunning: boolean) => {
        // console.log('isRunning', isRunning);
        setIsGeneratingGame(isRunning);
    };

    const handleSubmitSuggested = (selectedItems: { id: string; text: string; checked: boolean; interactive?: boolean }[]) => {
        // console.log('handleSubmitSuggested checked items:', selectedItems);
        // TODO: Implement AI logic to handle suggested items
        const autoPrompt = 'Implement next suggestions:';
        const prompt = autoPrompt + '\n' + selectedItems.map(item => '- ' + item.text).join('\n');
        // console.log('handleSubmitSuggested prompt:', prompt);
        handleGenerateFromUser(prompt);
    };

    const applyUpdatedPlanStatus = (newStatus: string, statusType?: string, copyFromUserFacing?: boolean) => {
        const newPlan = { plan: JSON.parse(currentPlan || "{}").plan, status: newStatus, statusType: statusType || '' };

        setCurrentPlan(JSON.stringify(newPlan));
    };

    const isTerminalPlanStatus = (status?: string | null) => {
        if (!status) return false;
        const terminal = [
            'Initial Working Build',
            'Second Round Testing Complete',
            'Add Complete',
            'Flow Complete',
            'Revision Complete',
            'Create Failed',
            'Edit Failed',
            'Cancelled',
        ];
        return terminal.some(t => status.includes(t));
    };

    const scheduleStreamDoneFallbackIfTerminal = (status?: string | null) => {
        if (!isTerminalPlanStatus(status)) return;
        const marker = lastRequestStartAtRef.current;
        const { isStreaming, activeSessionId } = getStreamUIState() as any;
        // If a client-side session is active, do not force-close the stream UI.
        if (activeSessionId) {
            return;
        }
        if (!isStreaming) {
            try { setStreamUIState({ isStreaming: false, streamPhase: 'Completed' }); } catch (_) {}
            return;
        }
        // Grace period: allow actual stream completion to arrive first
        setTimeout(() => {
            if (lastRequestStartAtRef.current !== marker) return; // newer request started
            const now = getStreamUIState();
            if (now.isStreaming) {
                try { setStreamUIState({ isStreaming: false, streamPhase: 'Completed' }); } catch (_) {}
            }
        }, 1500);
    };

    if (view === 'initial') {
        return <GameInitial
            config={config}
            instruction={instruction}
            onInstructionChange={handleInstructionChange}
            onGenerateFromInput={handleGenerateFromUser}
            aiToggle={aiToggle}
            onAiToggleChange={onToggleAI}
            onDebugTemplateClick={onDebugTemplateClick}
            user={user}
        />
    } else {
        // Handle constant changes for real-time preview
    const handleConstantChange = (updatedCode: GithubFormat) => {
        // Update the code state for real-time preview
        setLoadingMode(true);
        setCode(updatedCode);
        setCodeForAI(extractCodeForAI(updatedCode, filesForAIList) as AIFormat);
        setTimeout(() => {
            // console.log("Loading timeout complete, setting loadingMode to false");
            setLoadingMode(false);
        }, CODE_CHANGE_LOAD_TIME);
    };

    return <GameEdit
            loading={!user?.id}
            config={config}
            gameId={currentGameId}
            gameName={gameName}
            mode={mode}
            setMode={setMode}
            history={history}
            selectedHistoryIndex={selectedHistoryIndex}
            sandpackKey={sandpackKey}
            iframeErrors={iframeErrors}
            // Streaming props will be reimplemented with SSE
            isGeneratingName={isGeneratingName}
            onHomeClick={onHomeClick}
            onHistorySelect={onSelectHistoryState}
            onRefreshClick={refreshApp}
            handleGameNameChange={(e) => changeGameName(e.target.value)}
            handleInstructionChange={handleInstructionChange}
            handleGenerateFromInput={handleGenerateFromUser}
            handleIframeError={handleIframeError}
            fixErrors={fixErrors}
            code={loadingMode ? [] : code}
            originalCode={loadingMode ? [] : originalCode}
            plan={currentPlan}
            instruction={instruction}
            onConstantChange={handleConstantChange}
            onSaveGameSettings={(updatedCode) => handleSaveLocalCodeChange(updatedCode, 'balance')}
            onSaveCodeEdit={(updatedCode) => handleSaveLocalCodeChange(updatedCode, 'code')}
            onDeviceClick={handleDeviceClick}
            devicePreview={devicePreview}
            onAIStatusChange={handleAIStatusChange}
            isGeneratingGame={isGeneratingGame}
            isUpdatingGameArt={isUpdatingGameArt}
            onSubmitSuggested={handleSubmitSuggested}
            updatePlanStatus={applyUpdatedPlanStatus}
            setIsUpdatingGameArt={setIsUpdatingGameArt}
            gameArtTriggerReload={gameArtTriggerReload}
            incrementGameArtTriggerReload={incrementGameArtTriggerReload}
            currentGameHistoryId={historyEntryId}
            gameCreateCompleted={gameCreateCompleted}
            loadingMode={loadingMode}
            errorsForModal={loadingMode ? [] : iframeErrors}
            handleTryToFix={handleTryToFix}
            handleUndoClick={handleUndoClick}
            canUndo={canUndo}
            handleCloseErrorPopup={handleCloseErrorPopup}
            latestUserFacingHistoryId={latestUserFacingHistoryId}
            latestUserFacingCode={latestUserFacingCode}
            latestUserFacingPrompt={latestUserFacingPrompt}
            latestUserFacingStatus={latestUserFacingStatus}
            isViewingLatest={isViewingLatest}
            selectedEntrySnapshot={selectedEntrySnapshot}
            realtimeArtEvent={realtimeArtEvent || undefined}
            onStreamContext={(ctx) => {
                capturedStreamContextRef.current = ctx;
                if (pendingStreamCtxResolveRef.current) {
                    const resolve = pendingStreamCtxResolveRef.current;
                    pendingStreamCtxResolveRef.current = null;
                    resolve(ctx);
                }
            }}
        />
    }
};

const Game = () => {
    return (
        <StreamContextProvider>
            <FreeTrialTrigger colorMode="dark" />
            <GameInner />
        </StreamContextProvider>
    );
};

export default Game;
